import * as THREE from 'three'
import { OrbitControls } from 'three/addons/controls/OrbitControls.js'
import { vec4 } from 'three/tsl'
import { RectAreaLightNode } from 'three/webgpu'
import { RectAreaLightTexturesLib } from 'three/addons/lights/RectAreaLightTexturesLib.js'
import { TYRELL } from './config'
import { qualityBudget, BUDGET_MODES } from './TyrellQuality'
import { loadTyrell, disposeScene } from './TyrellAssets'
import TyrellCameraRig from './TyrellCameraRig'
import TyrellUI from './TyrellUI'
import { captureReference, REFERENCE_CAPTURE } from './TyrellCapture'
import { auditMaterialMaps } from './TyrellMaterialAudit'
import TyrellCameraPan from './TyrellCameraPan'
import TyrellNavigation from './TyrellNavigation'
import TyrellCollision from './TyrellCollision'
import TyrellLifecycle from './TyrellLifecycle'
import TyrellPerformance from './TyrellPerformance'
import TyrellLook, { applyColorReference } from './TyrellLook'
import TyrellLighting from './TyrellLighting'
import TyrellIndirect from './TyrellIndirect'
import { addColumnCores } from './TyrellColumnCores'
import { settleInstrumentCase } from './TyrellContacts'
import TyrellFloorReflection from './TyrellFloorReflection'
import TyrellSpecularEnvironment from './TyrellSpecularEnvironment'
import TyrellAtmosphere from './TyrellAtmosphere'
import TyrellLightVolume from './TyrellLightVolume'
import TyrellPostProcessing from './TyrellPostProcessing'
import TyrellComparison from './TyrellComparison'
import TyrellSky from './TyrellSky'
import TyrellLensFlare from './TyrellLensFlare'
import TyrellCity from './TyrellCity'
import TyrellBuildingLights from './TyrellBuildingLights'
import TyrellAirTraffic from './TyrellAirTraffic'
import TyrellNearTraffic from './TyrellNearTraffic'
import TyrellFlames from './TyrellFlames'
import TyrellStartup from './TyrellStartup'
import { measureGPUCapacity } from './TyrellGPUCapacity'

export default class E26902_BladeRunner {
    constructor() {
        this.type = 'WEBGPU_APP'
        this.profileEnabled = new URLSearchParams(location.search).get('profile') === '1'
        this.rendererOptions = { requireWebGPU: true, benchmarkOnStart: false, powerPreference: 'high-performance', trackTimestamp: this.profileEnabled }
        this.ownsMetrics = true
        this.quality = TYRELL.quality
        this.budgetMode = 'optimized'
        this.samples = []
        this.transitionDuration = 4.5
        this.comparison = new TyrellComparison()
        this.disposed = false
        this.lookProfile = TYRELL.materialLook
        this.exposureEV = 0
        this.studio = false
        this.lightingProfile = TYRELL.lightingProfile
    }
    mount(app) {
        if (this.ui) return
        this.app = app
        document.body.classList.add('tyrell-viewer', 'tyrell-reference')
        this.ui = new TyrellUI({
            restoreCamera: () => this.selectCamera(this.activeCameraIndex),
            transitionDuration: value => { if (Number.isFinite(value)) this.transitionDuration = Math.max(0, Math.min(10, value)) },
            navigation: enabled => this.setNavigation(enabled),
            enterNavigation: () => this.navigation?.enter(),
            navigationHeight: value => {
                if (!this.navigation || !Number.isFinite(value)) return
                this.navigation.height = Math.max(1.2, Math.min(1.9, value))
                if (this.navigation.enabled) this.navigation.camera.position.y = this.navigation.height
            },
            navigationSpeed: value => { if (this.navigation) this.navigation.speed = Math.max(.25, Math.min(5, value)) },
            budget: mode => { if (BUDGET_MODES.includes(mode)) { this.budgetMode = mode; this.setQuality(this.quality) } },
            camera: index => this.selectCamera(index), quality: name => this.setQuality(name),
            frame: enabled => { document.body.classList.toggle('tyrell-reference', enabled); app.size.update(); this.resetMetrics() },
            retry: () => this.rendererError ? location.reload() : this.load(),
            capture: () => { this.captureRequested = true }, report: () => this.downloadReport(),
            pan: settings => { (!this.developmentControls && !this.navigation?.enabled && !this.stageCamera?.transition && this.pan?.configure(settings)); this.resetMetrics() },
            center: () => this.selectCamera(this.activeCameraIndex),
            look: values => this.setLook(values),
            lighting: profile => this.setLighting(profile),
            reflection: enabled => { this.floorReflection?.setEnabled(enabled); this.resetMetrics() },
            reflectionMode: mode => { this.floorReflection?.setMode(mode); this.resetMetrics() },
            reflectionResolution: value => { this.floorReflection?.setResolution(value); this.resetMetrics() },
            reflectionUpdates: value => { this.floorReflection?.updates.setMode(value); this.resetMetrics() },
            specularEnvironment: enabled => { this.specularEnvironment?.setEnabled(enabled); this.resetMetrics() },
            volume: values => this.setVolume(values),
            post: values => { this.post?.configure(values); this.resetMetrics() },
            flare: values => { this.post?.configureFlare(values); this.resetMetrics() },
            sky: enabled => { this.sky?.setEnabled(enabled); this.resetMetrics() },
            skyHeight: value => { this.sky?.setHeightScale(value); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            skyRadial: value => { this.sky?.setRadialIntensity(value); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            city: enabled => { this.city?.setEnabled(enabled); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            buildingLights: values => { this.buildingLights?.configure(values); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            flames: values => { this.flames?.configure(values); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            nearTraffic: values => { this.nearTraffic?.configure(values); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            airTraffic: values => { this.airTraffic?.configure(values); this.floorReflection?.updates.invalidate(); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            measure: () => { if (this.developmentControls || this.navigation?.enabled || this.stageCamera?.transition) { this.ui.comparisonStatus('Selecciona una cámara fija y espera a que termine la transición.'); return } (!this.developmentControls && !this.navigation?.enabled && !this.stageCamera?.transition && this.pan?.reset()); this.resetMetrics(); this.measuring = true; this.ui.comparisonStatus('Midiendo: espera sin cambiar controles ni mover la cámara…') },
            clearMeasurements: () => { this.measuring = false; this.comparison.clear(); this.ui.showMeasurements([]); this.ui.comparisonStatus('Registro vacío.') },
            exportMeasurements: () => { const text = JSON.stringify({ note: 'RAF, CPU de envío y GPU de pases (opcional con profile=1). Comparar misma cámara y luz; consultar qualityBudget y resolución efectiva.', rows: this.comparison.rows }, null, 2); this.ui.showOutput(new Blob([text], { type: 'application/json' }), 'tyrell-comparacion.json', text) },
            atmosphere: values => { this.atmosphere?.configure(values); this.specularEnvironment?.invalidate(); this.resetMetrics() },
            indirect: mode => { this.indirect?.setMode(mode); this.setLighting(this.lightingProfile) },
            contribution: value => { this.lighting?.setContribution(value); this.indirect?.apply(this.lighting, this.studio); this.specularEnvironment?.invalidate(); this.resetMetrics() }
        })
        this.ui.setPanSettings(TYRELL.pan)
        this.ui.setEffectSettings(TYRELL.effects)
        this.ui.setSkyHeight(TYRELL.sky.heightScale)
        this.ui.setSkyRadial(TYRELL.sky.radialIntensity)
        this.ui.lightingSelect.value = this.lightingProfile
        this.ui.setLookSettings({ profile: this.lookProfile, ev: this.exposureEV, studio: this.studio })
        app.size.update()
        app.emitter.on('onAppKill', this.killListener = () => this.dispose())
    }
    onRendererProgress(app) { this.mount(app) }
    async measureGPUCapacity(renderer) {
        this.ui.status('Midiendo la capacidad de la GPU…')
        this.gpuCapacity = await measureGPUCapacity(renderer, () => this.disposed)
        if (!this.disposed) this.ui.setGPUCapacity(this.gpuCapacity)
    }
    onRendererError(error, app) {
        this.mount(app)
        this.rendererError = error
        this.ui.setBackend('WebGPU no disponible')
        this.ui.error(error, true)
    }
    init(app) {
        this.mount(app)
        this.scene = app.render.scene
        this.stageCamera = new TyrellCameraRig(app)
        this.pan = new TyrellCameraPan(this.stageCamera.get_camera(), TYRELL.pan)
        this.pointerMove = event => {
            if (event.pointerType !== 'mouse' || !this.revealed || !this.built || this.developmentControls || this.navigation?.enabled || this.stageCamera?.transition) return
            if (event.target?.closest?.('.tyrell-gui-panel footer, .tyrell-gui-toggle, .tyrell-controls, .tyrell-pan, .tyrell-look, dialog')) { this.pan.setPointer(0, 0); return }
            if (this.measuring) this.resetMetrics()
            this.pan.setPointer(event.clientX / window.innerWidth * 2 - 1, event.clientY / window.innerHeight * 2 - 1)
        }
        this.pointerLeave = () => this.pan.setPointer(0, 0)
        this.windowBlur = () => { if (!this.developmentControls && !this.navigation?.enabled && !this.stageCamera?.transition) this.pan.reset(); this.panTime = undefined }
        this.cameraKeyDown = event => {
            if (!this.revealed || !this.built || this.disposed || event.repeat || event.isComposing || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return
            if (event.target?.isContentEditable || event.target?.closest?.('input, textarea, select, [contenteditable], [role="textbox"]') || this.ui.dialog.open) return
            if (event.key.toLowerCase() === 'c') {
                event.preventDefault()
                this.setDevelopmentCamera(!this.developmentControls)
                return
            }
            if (!/^[0-9]$/.test(event.key)) return
            const index = event.key === '0' ? 9 : Number(event.key) - 1
            if (!this.cameras?.[index]) return
            event.preventDefault()
            this.selectCamera(index)
        }
        window.addEventListener('keydown', this.cameraKeyDown)
        window.addEventListener('pointermove', this.pointerMove)
        document.documentElement.addEventListener('pointerleave', this.pointerLeave)
        this.renderer = app.render.renderer
        this.navigation = new TyrellNavigation(this.stageCamera.get_camera(), this.renderer.domElement,
            message => this.ui.setNavigationState(this.navigation?.enabled, this.navigation?.locked, message), () => this.ui.dialog.open)
        this.performanceProbe = new TyrellPerformance(this.renderer, this.profileEnabled)
        this.colorReference = applyColorReference(this.renderer, this.exposureEV)
        this.post = new TyrellPostProcessing(this.renderer, this.scene)
        this.renderer.shadowMap.enabled = true
        this.renderer.shadowMap.type = THREE.PCFShadowMap
        this.renderer.shadowMap.transmitted = true
        this.renderer.setClearColor(0x171512, 1)
        this.ui.setBackend(`WebGPU · Three.js r${THREE.REVISION}`)
        this.setQuality(this.quality)
        this.resizeListener = () => this.resetMetrics()
        app.emitter.on('onAppSizeUpdate', this.resizeListener)
        this.lifecycle = new TyrellLifecycle({
            pauseInput: () => { this.navigation?.pause(); this.pan?.setPointer(0, 0) },
            resetTiming: () => { this.panTime = undefined; this.startup?.pause(); this.resetMetrics() },
            suspend: () => app.deactivate(), resume: () => app.activate(),
            ready: () => this.built, failed: () => !!this.rendererError
        })
        this.previousDeviceLost = this.renderer.onDeviceLost
        this.deviceLost = info => {
            if (this.disposed) return
            this.previousDeviceLost?.call(this.renderer, info)
            this.rendererError = new Error('Se ha perdido la conexión con la GPU. Recarga la página para recuperar el visor.')
            this.lifecycle.pause(); app.deactivate()
            this.ui.setBackend('WebGPU · conexión interrumpida')
            this.ui.error(this.rendererError, true)
        }
        this.renderer.onDeviceLost = this.deviceLost
        this.containerObserver = new ResizeObserver(() => {
            if (!this.disposed && app.$container.clientWidth > 0 && app.$container.clientHeight > 0) app.size.update()
        })
        this.containerObserver.observe(app.$container)
        this.load()
    }
    async load() {
        this.abort?.abort()
        this.abort = new AbortController()
        this.ui.progress.hidden = false
        this.revealed = false
        this.ui.beginStartup()
        this.ui.status('Cargando la oficina Tyrell…')
        try {
            this.asset = await loadTyrell(new URL(TYRELL.asset, new URL(this.app.pathPrefix, document.baseURI)).href, this.abort.signal,
                (received, total, parsing) => this.ui.status(parsing ? 'Preparando geometría, materiales y cámaras…' : `Cargando escenario · ${(received / 1e6).toFixed(1)}${total ? ' / ' + (total / 1e6).toFixed(1) : ''} MB`, total ? received / total : undefined))
            if (this.disposed) { disposeScene(this.asset.gltf.scene); return }
            this.skyTexture = await new THREE.TextureLoader().loadAsync(new URL(TYRELL.sky.texture, new URL(this.app.pathPrefix, document.baseURI)).href)
            this.skyTexture.colorSpace = THREE.SRGBColorSpace
            this.skyTexture.wrapS = THREE.ClampToEdgeWrapping
            if (this.disposed) { this.skyTexture.dispose(); disposeScene(this.asset.gltf.scene); return }
            this.app.emitter.emit('onProjectLoaded', {})
        } catch (error) {
            if (error.name === 'AbortError' || this.disposed) return
            console.error('[Tyrell] Carga:', error)
            // A parse/build failure must leave retry without a duplicate scene.
            disposeScene(this.asset?.gltf.scene)
            this.asset = null
            this.fill?.removeFromParent()
            this.ui.error(error)
        }
    }
    build() {
        this.world3D = this.asset.gltf.scene
        this.navigation.collision = new TyrellCollision(this.world3D)
        this.columnCores = addColumnCores(this.world3D)
        this.contactRepair = settleInstrumentCase(this.world3D)
        this.city = new TyrellCity(this.world3D)
        this.buildingLights = new TyrellBuildingLights(this.world3D)
        this.airTraffic = new TyrellAirTraffic(this.world3D)
        this.nearTraffic = new TyrellNearTraffic(this.world3D)
        this.flames = new TyrellFlames(this.world3D, this.city)
        this.floorReflection = new TyrellFloorReflection(this.world3D)
        this.scene.add(this.world3D)
        this.world3D.updateMatrixWorld(true)
        this.materialAudit = auditMaterialMaps(this.world3D)
        this.look = new TyrellLook(this.world3D)
        this.cameras = []
        this.modelStats = { meshes: 0, triangles: 0, cameras: 0 }
        this.world3D.traverse(object => {
            if (object.isPerspectiveCamera) this.cameras.push(object)
            if (object.isMesh) {
                this.modelStats.meshes++
                this.modelStats.triangles += (object.geometry.index?.count || object.geometry.attributes.position.count) / 3
                object.receiveShadow = true
                // Keep the distant pyramid/sky out of the room's shadow pass.
                object.getWorldPosition(_position)
                object.castShadow = !object.name.startsWith('City /') && _position.length() < 50
                if (object.name.startsWith('City /')) object.receiveShadow = false
                const materials = Array.isArray(object.material) ? object.material : [object.material]
                for (const material of materials) {
                    if (material.name === 'Crystal') material.castShadowNode = vec4(0, 0, 0, TYRELL.shadows.glassOpacity)
                }
            }
            if (object.isDirectionalLight) {
                this.sun = object
                object.userData.exportedIntensity = object.intensity
                object.intensity = TYRELL.sunIntensity
                object.castShadow = true
                Object.assign(object.shadow.camera, TYRELL.shadows.camera)
                object.shadow.bias = TYRELL.shadows.bias
                object.shadow.normalBias = TYRELL.shadows.normalBias
                object.shadow.radius = TYRELL.shadows.radius
                object.shadow.camera.updateProjectionMatrix()
            }
        })
        this.cameras.sort((a, b) => a.name.localeCompare(b.name))
        this.modelStats.cameras = this.cameras.length
        if (!this.cameras.length) throw new Error('El GLB no contiene cámaras de perspectiva.')
        this.fill = new THREE.HemisphereLight(0xffdab0, 0x797e8a, TYRELL.hemisphereIntensity)
        this.fill.name = 'Tyrell / relleno provisional'
        this.scene.add(this.fill)
        if (ltcUsers++ === 0) RectAreaLightNode.setLTC(RectAreaLightTexturesLib.init())
        this.usesLTC = true
        this.areaLights = TYRELL.areaFills.map(({ position, target, color, intensity, size }) => {
            const light = new THREE.RectAreaLight(color, intensity, size, size)
            light.name = 'Tyrell / área provisional'
            light.position.set(...position)
            light.lookAt(new THREE.Vector3(...target))
            this.scene.add(light)
            return light
        })
        this.lighting = new TyrellLighting(this.world3D, this.sun, this.fill, this.areaLights)
        this.post.flare = new TyrellLensFlare(this.lighting.disc, () => !this.studio && this.sun.visible && this.sun.intensity > 0)
        this.indirect = new TyrellIndirect(this.scene)
        this.specularEnvironment = new TyrellSpecularEnvironment(this.scene, this.world3D, this.floorReflection)
        this.volume = new TyrellLightVolume(() => this.sun)
        this.atmosphere = new TyrellAtmosphere(this.scene, this.world3D, this.volume)
        this.sky = new TyrellSky(this.scene, this.lighting.sky, this.skyTexture, {
            ...TYRELL.sky, aspect: TYRELL.referenceAspect,
            camera: this.cameras.find(camera => /^(?:Camera|CAM)[ _]D(?:\W|$)/i.test(camera.name))
        })
        this.setLighting(this.lightingProfile)
        this.studioLights = new THREE.Group()
        this.studioLights.name = 'Tyrell / estudio de materiales'
        this.studioLights.add(new THREE.HemisphereLight(0xffffff, 0x777777, 1.2))
        for (const [position, target, intensity] of [
            [[-5, 6, -5], [0, 1, -9], 3], [[6, 4, -12], [0, 1, -8], 1.5]
        ]) {
            const light = new THREE.RectAreaLight(0xffffff, intensity, 8, 8)
            light.position.set(...position); light.lookAt(new THREE.Vector3(...target)); this.studioLights.add(light)
        }
        this.scene.add(this.studioLights)
        this.reviewBackground = []
        this.world3D.traverse(object => {
            if (object.isMesh && (object.name.startsWith('Cielo') || object.name.startsWith('Sol'))) {
                this.reviewBackground.push({ object, visible: object.visible })
            }
        })
        this.setLook({ profile: this.lookProfile, studio: this.studio })
        this.selectCamera(Math.max(0, this.cameras.findIndex(camera => camera.name.startsWith(TYRELL.initialCamera.replaceAll(' ', '_')) || camera.name.startsWith(TYRELL.initialCamera))))
        this.setQuality(this.quality)
        this.built = true
        // CanvasApp activates after build; run visibility reconciliation afterwards.
        queueMicrotask(() => { if (!this.disposed && document.hidden) this.lifecycle.sync() })
        this.floorReflection.setEnabled(TYRELL.effects.floorReflection)
        this.specularEnvironment.setEnabled(TYRELL.effects.specularEnvironment)
        this.atmosphere.configure({ exterior: TYRELL.effects.exterior, interior: TYRELL.effects.interior })
        this.setVolume({ enabled: TYRELL.effects.volume })
        this.post.configure({ bloom: TYRELL.effects.bloom, grade: TYRELL.effects.grade })
        this.startup = new TyrellStartup({ fadeMs: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 1500 })
        this.ui.status('Estabilizando la escena con los efectos activos…')
        this.resetMetrics()
    }
    selectCamera(index) {
        if (!this.cameras?.[index]) return
        this.stopDevelopmentCamera()
        this.navigation?.setEnabled(false)
        this.activeCameraIndex = index
        if (this.ui?.cameraSelect) this.ui.cameraSelect.value = String(index)
        const duration = this.built && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? this.transitionDuration : 0
        this.stageCamera.setSource(this.cameras[index], duration)
        this.pan?.setReference(this.stageCamera.destination)
        this.app.render.set_stageCamera(this.stageCamera.get_camera())
        this.app.dev.hide_dev_camera()
        this.resetMetrics()
    }
    stopDevelopmentCamera() {
        this.developmentControls?.dispose()
        this.developmentControls = null
    }
    setDevelopmentCamera(enabled) {
        if (!this.built || this.disposed) return
        if (!enabled) { this.selectCamera(this.activeCameraIndex); return }
        if (this.developmentControls) return
        this.navigation?.setEnabled(false)
        this.stageCamera.cancelTransition()
        this.app.dev.hide_dev_camera()
        const camera = this.stageCamera.get_camera()
        // Keep the current viewing direction, including any authored camera roll.
        camera.up.set(0, 1, 0).applyQuaternion(camera.quaternion)
        const target = camera.getWorldDirection(new THREE.Vector3())
            .multiplyScalar(this.pan.settings.targetDistance).add(camera.position)
        const controls = new OrbitControls(camera)
        controls.target.copy(target)
        controls.screenSpacePanning = true
        controls.connect(this.renderer.domElement)
        controls.update()
        controls.addEventListener('change', () => this.resetMetrics())
        this.developmentControls = controls
        this.panTime = undefined
        this.resetMetrics()
    }
    setNavigation(enabled) {
        if (!this.built) return
        if (!enabled) { this.selectCamera(this.activeCameraIndex); return }
        this.stopDevelopmentCamera()
        this.stageCamera.cancelTransition()
        this.navigation.setEnabled(true)
        this.panTime = undefined
        this.resetMetrics()
    }
    setVolume(values) {
        if (!this.volume || !this.atmosphere) return
        this.volume.configure(values)
        this.atmosphere.refreshVolume()
        this.specularEnvironment.invalidate()
        this.resetMetrics()
    }
    renderFrame(camera) {
        const start = this.profileEnabled ? performance.now() : 0
        if (this.post) this.post.render(camera)
        else this.renderer.render(this.scene, camera)
        if (this.firstFrameMs === undefined) this.firstFrameMs = performance.now()
        this.performanceProbe?.frame(performance.now() - start, this.warmup === 0 && !document.hidden)
    }
    setLighting(profile) {
        this.specularEnvironment?.invalidate()
        if (!['provisional', 'tyrell-light-v1', 'tyrell-light-v2', 'tyrell-light-v3'].includes(profile)) return
        this.lightingProfile = profile
        this.lighting?.apply(profile)
        this.sky?.configureRadialDarkening(
            this.cameras?.find(camera => /^CAM[ _]?0?1(?:\D|$)/i.test(camera.name)),
            this.lighting?.disc, TYRELL.referenceAspect,
            this.cameras?.find(camera => /^CAM[ _]?0?2(?:\D|$)/i.test(camera.name)))
        this.indirect?.apply(this.lighting, this.studio)
        this.resetMetrics()
    }
    setLook(values) {
        this.specularEnvironment?.invalidate()
        if (['imported', 'tyrell-v1'].includes(values.profile)) {
            this.lookProfile = values.profile
            this.look?.apply(this.lookProfile)
            if (this.world3D) this.materialAudit = auditMaterialMaps(this.world3D)
        }
        if (typeof values.floorNormal === 'boolean') {
            this.look?.setFloorNormal(values.floorNormal)
            if (this.world3D) this.materialAudit = auditMaterialMaps(this.world3D)
        }
        if (Number.isFinite(values.ev)) this.exposureEV = Math.max(-2, Math.min(2, values.ev))
        if (this.renderer) this.colorReference = applyColorReference(this.renderer, this.exposureEV)
        if (typeof values.studio === 'boolean') {
            this.studio = values.studio
            this.sky?.setStudio(this.studio)
            if (this.lighting) { this.lighting.apply(this.lightingProfile); this.indirect?.apply(this.lighting, this.studio) }
        }
        if (this.studioLights) {
            this.studioLights.visible = this.studio
            if (this.sun) this.sun.visible = !this.studio
            this.fill.visible = !this.studio
            this.areaLights.forEach(light => { light.visible = !this.studio })
            this.reviewBackground.forEach(({ object, visible }) => { object.visible = !this.studio && visible })
            this.sky?.apply()
            this.renderer.setClearColor(this.studio ? 0x202020 : 0x171512, 1)
        }
        this.resetMetrics()
    }
    setQuality(name) {
        const profile = qualityBudget(name, this.budgetMode)
        if (!profile) return
        this.quality = name
        this.volume?.setQuality(name, profile.volumeSteps)
        this.post?.setQuality(name)
        this.floorReflection?.setResolution(this.floorReflection.resolution, name, profile.reflectionScale)
        this.specularEnvironment?.invalidate()
        this.renderer?.setPixelRatio(profile.pixelRatio)
        this.app.render?.update_resize()
        if (this.sun && this.sun.shadow.mapSize.x !== profile.shadowSize) {
            // A new light identity rebuilds r182's cached shadow nodes and bindings.
            // Keep the authored transform/target; LightShadow.clone omits GPU targets.
            const previous = this.sun
            const replacement = previous.clone(false)
            replacement.target = previous.target
            replacement.shadow.mapSize.setScalar(profile.shadowSize)
            previous.parent.add(replacement)
            previous.removeFromParent()
            previous.dispose()
            this.sun = replacement
            if (this.lighting) this.lighting.sun = replacement
        }
        this.resetMetrics()
    }
    resetMetrics() {
        this.performanceProbe?.reset()
        if (this.measuring) this.ui.comparisonStatus('Medición cancelada por un cambio de configuración. Vuelve a medir.')
        this.measuring = false
        this.floorReflection?.updates.invalidate()
        this.samples = []; this.warmup = 60; this.previousFrame = undefined; this.metrics = undefined
        if (this.built) this.ui.metrics('Estabilizando la medición…')
    }
    update_RAF() {
        const now = performance.now()
        if (this.startup && !this.revealed && !this.rendererError) {
            if (document.hidden) this.startup.pause()
            else {
                this.startup.frame(now)
                this.ui.setStartupOpacity(this.startup.opacity)
                if (this.startup.phase !== 'warming') this.ui.statusBox.hidden = true
                if (this.startup.phase === 'ready') {
                    this.revealed = true
                    this.ui.ready(this.cameras, this.activeCameraIndex)
                }
            }
        }
        const volumeNode = this.volume?.node
        if (this.volume?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) this.floorReflection?.updates.invalidate()
        if (this.buildingLights?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) {
            this.floorReflection?.updates.invalidate()
            this.specularEnvironment?.invalidate()
        }
        if (this.airTraffic?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) {
            this.floorReflection?.updates.invalidate()
            if (this.airTraffic.environmentNeedsRefresh) this.specularEnvironment?.invalidate()
        }
        if (this.flames?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) {
            this.floorReflection?.updates.invalidate()
            if (this.flames.environmentNeedsRefresh) this.specularEnvironment?.invalidate()
        }
        if (this.nearTraffic?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) {
            this.floorReflection?.updates.invalidate()
            if (this.nearTraffic.environmentNeedsRefresh) this.specularEnvironment?.invalidate()
        }
        if (this.volume?.node !== volumeNode) { this.atmosphere.refreshVolume(); this.specularEnvironment.invalidate(); this.resetMetrics() }
        if (this.sky) this.sky.withOriginalBackground(() => this.specularEnvironment?.update(this.renderer))
        else this.specularEnvironment?.update(this.renderer)
        if (this.developmentControls) this.developmentControls.update()
        else if (this.navigation?.enabled) this.navigation.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)
        else if (!this.stageCamera.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)) this.pan?.update(this.panTime === undefined ? 0 : (now - this.panTime) / 1000)
        this.panTime = now
        if (this.previousFrame !== undefined && !document.hidden) {
            if (this.warmup > 0) this.warmup--
            else {
                this.samples.push(now - this.previousFrame)
                if (this.samples.length > 120) this.samples.shift()
            }
        }
        this.previousFrame = now
        if (this.samples.length >= 30 && (!this.lastReport || now - this.lastReport > 1000)) {
            this.lastReport = now
            const mean = this.samples.reduce((a, b) => a + b, 0) / this.samples.length
            const sorted = [...this.samples].sort((a, b) => a - b)
            const size = this.renderer.getDrawingBufferSize(new THREE.Vector2())
            this.metrics = {
                fps: +(1000 / mean).toFixed(1), frameMeanMs: +mean.toFixed(2),
                frameP95Ms: +sorted[Math.floor((sorted.length - 1) * 0.95)].toFixed(2),
                samples: this.samples.length, resolution: [size.x, size.y],
                drawCalls: this.renderer.info.render.drawCalls, renderedTriangles: this.renderer.info.render.triangles,
                geometries: this.renderer.info.memory.geometries, textures: this.renderer.info.memory.textures
            }
            this.ui.metrics(`${this.metrics.fps} FPS · ${this.metrics.frameMeanMs} ms/fotograma · ${size.x} × ${size.y} · ${this.metrics.drawCalls} dibujos · ${Math.round(this.metrics.renderedTriangles).toLocaleString('es')} triángulos/pasadas`)
            if (this.measuring && this.metrics.samples === 120) {
                this.measuring = false
                this.comparison.add(this.getDiagnostics())
                this.ui.showMeasurements(this.comparison.rows)
                this.ui.comparisonStatus('Medición guardada. Cambia un efecto y vuelve a medir; conserva cámara, calidad y encuadre.')
            }
        }
        if (this.captureRequested) {
            this.captureRequested = false
            const camera = this.activeCameraIndex + 1
            const atmosphere = this.atmosphere.settings
            const filename = `tyrell-camara-${camera}-${this.lookProfile}-${this.studio ? 'estudio' : 'escena'}-${this.lightingProfile}-${this.indirect?.mode || 'reference'}-${this.lighting.contribution || 'all'}-EV${this.exposureEV}-normal${this.look.floorNormal ? 'on' : 'off'}-reflejo${this.floorReflection.enabled ? 'on' : 'off'}-${this.floorReflection.mode}-ext${atmosphere.exterior ? atmosphere.exteriorStrength : 0}-int${atmosphere.interior ? atmosphere.interiorStrength : 0}-volume${this.volume.settings.enabled ? this.volume.settings.strength : 0}-dust${this.volume.settings.dust ? this.volume.settings.speed : 0}-1920x800.png`
            captureReference(this.renderer, this.scene, (this.developmentControls || this.navigation?.enabled) ? this.stageCamera.get_camera() : this.pan.referenceCamera, camera => this.renderFrame(camera))
                .then(blob => { if (!this.disposed) this.ui.showOutput(blob, filename.replace('-1920x800', `-bloom${this.post.settings.bloom ? this.post.settings.strength : 0}-flare${this.post.flare.settings.enabled ? this.post.flare.settings.intensity : 0}-grade${this.post.settings.grade ? this.post.settings.gradeStrength : 0}-1920x800`)) })
                .catch(error => {
                    console.error('[Tyrell] Captura:', error)
                    if (!this.disposed) this.ui.showOutput(new Blob([error.message], { type: 'text/plain' }), 'tyrell-error-captura.txt', error.message)
                })
            this.resetMetrics()
        }
    }
    getDiagnostics() {
        const camera = this.stageCamera?.get_camera()
        const info = this.renderer?.backend?.device?.adapterInfo
        return {
            phase: 9, step: '9.5', date: new Date().toISOString(), backend: this.renderer?.backend?.isWebGPUBackend ? 'WebGPU' : 'unavailable',
            referenceCapture: { ...REFERENCE_CAPTURE, aspect: 2.4 },
            three: THREE.REVISION, userAgent: navigator.userAgent, secureContext: isSecureContext,
            adapter: info ? { vendor: info.vendor, architecture: info.architecture, device: info.device, description: info.description } : null,
            benchmarkOnStart: true, gpuCapacity: this.gpuCapacity || null, startup: this.startup?.diagnostics() || null,
            qualityBudget: { mode: this.budgetMode, ...qualityBudget(this.quality, this.budgetMode) }, quality: this.quality, exposure: this.renderer?.toneMappingExposure,
            model: this.modelStats, asset: { path: TYRELL.asset, bytes: this.asset?.bytes, loadMs: this.asset?.loadMs },
            columnRepair: this.columnCores?.userData.columnRepair || null,
            contactRepair: this.contactRepair || null,
            floorReflection: this.floorReflection?.diagnostics() || null,
            specularEnvironment: this.specularEnvironment?.diagnostics() || null,
            atmosphere: this.atmosphere?.diagnostics() || null,
            lightVolume: this.volume?.diagnostics() || null,
            postProcessing: this.post?.diagnostics() || null,
            sky: this.sky?.diagnostics() || null,
            city: this.city?.diagnostics() || null,
            buildingLights: this.buildingLights?.diagnostics() || null,
            airTraffic: this.airTraffic?.diagnostics() || null,
            nearTraffic: this.nearTraffic?.diagnostics() || null,
            flames: this.flames?.diagnostics() || null,
            comparison: this.comparison.rows,
            optionalFinish: { grain: false, vignette: false, depthOfField: false, decision: '6.6: retain clean image; preserve stone, floor reflections and architectural depth' },
            materialAudit: this.materialAudit || null,
            colorReference: this.colorReference,
            lighting: this.lighting?.diagnostics(),
            indirect: this.indirect?.diagnostics(),
            shadows: { ...TYRELL.shadows, mapSize: this.sun?.shadow.mapSize.toArray(), transmitted: this.renderer.shadowMap.transmitted },
            materialLook: this.look?.diagnostics(), reviewLighting: this.studio ? 'neutral-studio' : this.lightingProfile,
            collision: this.navigation?.collision?.diagnostics(),
            lifecycle: { hidden: document.hidden, focused: document.hasFocus(), gpuLost: !!this.rendererError, target: 'Desktop with dedicated GPU, keyboard and mouse; WebGPU on localhost or HTTPS' },
            cameraTransition: { duration: this.transitionDuration, active: !!this.stageCamera?.transition },
            navigation: { mode: this.developmentControls ? 'development' : this.navigation?.enabled ? 'free' : 'fixed', locked: this.navigation?.locked, speed: this.navigation?.speed },
            cameraPan: this.pan?.diagnostics() || null,
            camera: camera ? { name: camera.name, position: camera.position.toArray(), quaternion: camera.quaternion.toArray(), fov: camera.fov, aspect: camera.aspect, near: camera.near, far: camera.far } : null,
            metrics: this.metrics || null,
            performance: this.performanceProbe?.report(),
            loading: { firstRenderedFrameSinceNavigationMs: this.firstFrameMs, glbFetchDecodeMs: this.asset?.loadMs, glbBytes: this.asset?.bytes, note: 'First render submitted, not guaranteed presented. Cache/network conditions must be recorded separately.' },
            metricNote: 'Intervalos RAF después de 60 fotogramas de calentamiento, ventana móvil de hasta 120 muestras. Incluyen presentación; no son tiempo GPU ni un benchmark de hardware.',
            pending: ['Fase 8: optimización y validación final']
        }
    }
    downloadReport() {
        const text = JSON.stringify(this.getDiagnostics(), null, 2)
        this.ui.showOutput(new Blob([text], { type: 'application/json' }), 'tyrell-diagnostico.json', text)
    }
    dispose() {
        if (this.disposed) return
        this.disposed = true
        this.abort?.abort()
        window.removeEventListener('keydown', this.cameraKeyDown)
        window.removeEventListener('pointermove', this.pointerMove)
        document.documentElement.removeEventListener('pointerleave', this.pointerLeave)
        this.performanceProbe?.dispose()
        this.lifecycle?.dispose()
        this.containerObserver?.disconnect()
        if (this.renderer?.onDeviceLost === this.deviceLost) this.renderer.onDeviceLost = this.previousDeviceLost
        this.stopDevelopmentCamera()
        this.navigation?.dispose()
        this.stageCamera?.dispose()
        this.app.emitter.off('onAppKill', this.killListener)
        if (this.resizeListener) this.app.emitter.off('onAppSizeUpdate', this.resizeListener)
        this.specularEnvironment?.dispose()
        this.post?.dispose()
        this.sky?.dispose()
        this.atmosphere?.dispose()
        this.buildingLights?.dispose()
        this.volume?.dispose()
        this.floorReflection?.dispose()
        disposeScene(this.world3D)
        this.fill?.removeFromParent()
        this.fill?.dispose()
        this.indirect?.dispose()
        this.areaLights?.forEach(light => { light.removeFromParent(); light.dispose() })
        this.studioLights?.traverse(object => { if (object.isLight) object.dispose() })
        this.studioLights?.removeFromParent()
        this.look?.original.clear()
        if (this.usesLTC && --ltcUsers === 0) {
            for (const name of ['LTC_FLOAT_1', 'LTC_FLOAT_2', 'LTC_HALF_1', 'LTC_HALF_2']) RectAreaLightTexturesLib[name].dispose()
        }
        this.ui?.dispose()
        document.body.classList.remove('tyrell-viewer', 'tyrell-reference')
    }
}
const _position = new THREE.Vector3()
let ltcUsers = 0
